export default class BizminerRatios {
    constructor({
        yearsFormatted = [],
        cash = [],
        marketableSecurities = [],
        accountsReceivable = [],
        inventory = [],
        accountsPayable = [],
        loansNotesPayable = [],
        totalLongTermAssets = [],
        totalCurrentAssets = [],
        totalAssets = [],
        totalCurrentLiabilities = [],
        totalLongTermLiabilities = [],
        totalLiabilities = [],
        totalEquity = [],
        totalSales = [],
        costOfSales = [],
        laborCost = [],
        wage = [],
        yearlyGrossProfitMargin = [],
        operatingIncome = [],
        interestExpense = [],
        yearlyPreTaxNetProfit = [],
        yearlyAfterTaxNetProfit = [],
        EBITDA = [],
        yearlyDepreciationDepletionAmortization = [],
        yearlyOfficersCompensation = [],
        averageEmployees = [],
    }) {
        this.yearsFormatted = yearsFormatted;
        this.cash = cash;
        this.marketableSecurities = marketableSecurities;
        this.accountsReceivable = accountsReceivable;
        this.inventory = inventory;
        this.accountsPayable = accountsPayable;
        this.loansNotesPayable = loansNotesPayable;
        this.totalLongTermAssets = totalLongTermAssets;
        this.totalCurrentAssets = totalCurrentAssets;
        this.totalAssets = totalAssets;
        this.totalCurrentLiabilities = totalCurrentLiabilities;
        this.totalLongTermLiabilities = totalLongTermLiabilities;
        this.totalLiabilities = totalLiabilities;
        this.totalEquity = totalEquity;
        this.totalSales = totalSales;
        this.costOfSales = costOfSales;
        this.laborCost = laborCost;
        this.wage = wage;
        this.yearlyGrossProfitMargin = yearlyGrossProfitMargin;
        this.operatingIncome = operatingIncome;
        this.interestExpense = interestExpense;
        this.yearlyPreTaxNetProfit = yearlyPreTaxNetProfit;
        this.yearlyAfterTaxNetProfit = yearlyAfterTaxNetProfit;
        this.EBITDA = EBITDA;
        this.yearlyDepreciationDepletionAmortization = yearlyDepreciationDepletionAmortization;
        this.yearlyOfficersCompensation = yearlyOfficersCompensation;
        this.averageEmployees = averageEmployees;
    }

    get yearlyNetWorkingCapital() {
        const { totalCurrentAssets, totalCurrentLiabilities } = this;
        return this.getArraySum(totalCurrentAssets, totalCurrentLiabilities, { minus: true });
    }

    get yearlyWagesAndLaborCost() {
        const { laborCost, wage } = this;
        return this.getArraySum(laborCost, wage);
    }

    get yearlyAverageEmployeeWage() {
        const { averageEmployees, yearlyWagesAndLaborCost } = this;
        return this.getArrayDivide(yearlyWagesAndLaborCost, averageEmployees);
    }

    get yearlyDiscretionaryOwnerEarnings() {
        const { yearlyOfficersCompensation, yearlyDepreciationDepletionAmortization, yearlyPreTaxNetProfit } = this;
        return this.getArraySum(
            this.getArraySum(yearlyOfficersCompensation, yearlyDepreciationDepletionAmortization),
            yearlyPreTaxNetProfit
        );
    }
    
    get yearlyAccountsPayableBusinessRevenue() {
        const { accountsPayable, totalSales } = this;
        return this.getArrayDivide(accountsPayable, totalSales);
    }

    get yearlyAccountsPayabletoSales() {
        return this.yearlyAccountsPayableBusinessRevenue;
    }

    get yearlyCurrentRatio() {
        const { totalCurrentAssets, totalCurrentLiabilities } = this;
        return this.getArrayDivide(totalCurrentAssets, totalCurrentLiabilities);
    }

    get yearlyQuickRatio() {
        const { cash, marketableSecurities, accountsReceivable, totalCurrentLiabilities } = this;
        return this.getArrayDivide(
            this.getArraySum(this.getArraySum(cash, marketableSecurities), accountsReceivable),
            totalCurrentLiabilities
        );
    }

    get yearlyDaysPayable() {
        const { costOfSales, accountsPayable } = this;
        return this.getMultiply(this.getArrayDivide(accountsPayable, costOfSales), 365);
    }

    get yearlyCurrentLiabilitiesInventory() {
        const { totalCurrentLiabilities, inventory } = this;
        return this.getArrayDivide(totalCurrentLiabilities, inventory);
    }

    get yearlyNetWorkingCapitalBusinessRevenue() {
        const { yearlyNetWorkingCapital, totalSales } = this;
        return this.getArrayDivide(yearlyNetWorkingCapital, totalSales);
    }

    get yearlyWorkingCapitaltoSales() {
        return this.yearlyNetWorkingCapitalBusinessRevenue;
    }

    get yearlyCostofSalesAccountsPayable() {
        const { costOfSales, accountsPayable } = this;
        return this.getArrayDivide(costOfSales, accountsPayable);
    }

    get yearlyCostofSalesInventory() {
        const { costOfSales, inventory } = this;
        return this.getArrayDivide(costOfSales, inventory);
    }

    get yearlyEBITDABusinessRevenue() {
        const { EBITDA, totalSales } = this;
        return this.getArrayDivide(EBITDA, totalSales);
    }

    get yearlyPreTaxReturnOnAssets() {
        const { yearlyPreTaxNetProfit, totalAssets } = this;
        return this.getArrayDivide(yearlyPreTaxNetProfit, totalAssets);
    }

    get yearlyPreTaxReturnonNetWorth() {
        const { yearlyPreTaxNetProfit, totalEquity } = this;
        return this.getArrayDivide(yearlyPreTaxNetProfit, totalEquity);
    }

    get yearlyPreTaxReturnonBusinessRevenue() {
        const { yearlyPreTaxNetProfit, totalSales } = this;
        return this.getArrayDivide(yearlyPreTaxNetProfit, totalSales);
    }

    get yearlyAfterTaxReturnOnAssets() {
        const { yearlyAfterTaxNetProfit, totalAssets } = this;
        return this.getArrayDivide(yearlyAfterTaxNetProfit, totalAssets);
    }

    get yearlyAfterTaxReturnOnNetWorth() {
        const { yearlyAfterTaxNetProfit, totalEquity } = this;
        return this.getArrayDivide(yearlyAfterTaxNetProfit, totalEquity);
    }

    get yearlyAfterTaxReturnOnBusinessRevenue() {
        const { yearlyAfterTaxNetProfit, totalSales } = this;
        return this.getArrayDivide(yearlyAfterTaxNetProfit, totalSales);
    }

    get yearlyPreTaxProfitEmployee() {
        const { yearlyPreTaxNetProfit, averageEmployees } = this;
        return this.getArrayDivide(yearlyPreTaxNetProfit, averageEmployees);
    }

    get yearlyOwnerEarningsEmployee() {
        const { yearlyDiscretionaryOwnerEarnings, averageEmployees } = this;
        return this.getArrayDivide(yearlyDiscretionaryOwnerEarnings, averageEmployees);
    }

    get yearlyAssetsBusinessRevenue() {
        const { totalAssets, totalSales } = this;
        return this.getArrayDivide(totalAssets, totalSales);
    }

    get yearlyDaysInventory() {
        const { costOfSales, inventory } = this;
        return this.getMultiply(this.getArrayDivide(inventory, costOfSales), 365);
    }

    get yearlyDaysReceivables() {
        const { totalSales, accountsReceivable } = this;
        return this.getMultiply(this.getArrayDivide(accountsReceivable, totalSales), 365);
    }

    get yearlyCurrentAssetTurnover() {
        const { totalSales, totalCurrentAssets } = this;
        return this.getArrayDivide(totalSales, totalCurrentAssets);
    }

    get yearlyFixedAssetTurnover() {
        const { totalSales, totalLongTermAssets } = this;
        return this.getArrayDivide(totalSales, totalLongTermAssets);
    }

    get yearlySalesPerEmployee() {
        const { totalSales, averageEmployees } = this;
        return this.getArrayDivide(totalSales, averageEmployees);
    }

    get yearlyInventoryTurnover() {
        const { totalSales, inventory } = this;
        return this.getArrayDivide(totalSales, inventory);
    }

    get yearlyReceivablesTurnover() {
        const { totalSales, accountsReceivable } = this;
        return this.getArrayDivide(totalSales, accountsReceivable);
    }

    get yearlyPayablesTurnover() {
        const { totalSales, accountsPayable } = this;
        return this.getArrayDivide(totalSales, accountsPayable);
    }

    get yearlyTotalAssetTurnover() {
        const { totalSales, totalAssets } = this;
        return this.getArrayDivide(totalSales, totalAssets);
    }

    get yearlyWorkingCapitalTurnover() {
        const { totalSales, yearlyNetWorkingCapital } = this;
        return this.getArrayDivide(totalSales, yearlyNetWorkingCapital);
    }

    get yearlyDaysWorkingCapital() {
        const { yearlyWorkingCapitalTurnover } = this;
        return this.getArrayDivide(365, yearlyWorkingCapitalTurnover);
    }

    get yearlyCashTurnover() {
        const { totalSales, cash } = this;
        return this.getArrayDivide(totalSales, cash);
    }

    get yearlyEfficiencyIndex() {
        const { yearlySalesPerEmployee, yearlyAverageEmployeeWage } = this;
        return this.getArrayDivide(yearlySalesPerEmployee, yearlyAverageEmployeeWage);
    }

    get yearlyInterestCoverage() {
        const { EBITDA, interestExpense } = this;
        return this.getArrayDivide(EBITDA, interestExpense);
    }

    get yearlyCurrentLiabilitiesNetWorth() {
        const { totalCurrentLiabilities, totalEquity } = this;
        return this.getArrayDivide(totalCurrentLiabilities, totalEquity);
    }

    get yearlyLongTermLiabilitiesNetWorth() {
        const { totalLongTermLiabilities, totalEquity } = this;
        return this.getArrayDivide(totalLongTermLiabilities, totalEquity);
    }

    get yearlyLoansNotesPayableNetWorth() {
        const { loansNotesPayable, totalEquity } = this;
        return this.getArrayDivide(loansNotesPayable, totalEquity);
    }

    get yearlyModifiedZScore() {
        const {
            operatingIncome,
            yearlyNetWorkingCapital,
            yearlyDiscretionaryOwnerEarnings,
            totalEquity,
            totalAssets,
            totalLiabilities
        } = this;
        return this.getArraySum(
            this.getArraySum(
                this.getMultiply(this.getArrayDivide(operatingIncome, totalAssets), 6.72),
                this.getMultiply(this.getArrayDivide(totalEquity, totalLiabilities), 1.05)
            ),
            this.getArraySum(
                this.getMultiply(this.getArrayDivide(yearlyNetWorkingCapital, totalAssets), 6.5),
                this.getMultiply(this.getArrayDivide(yearlyDiscretionaryOwnerEarnings, totalAssets), 3.26)
            )
        );
    }

    get yearlyTotalLiabilitiesNetWorth() {
        const { totalLiabilities, totalEquity } = this;
        return this.getArrayDivide(totalLiabilities, totalEquity);
    }

    get yearlyFixedAssetsNetWorth() {
        const { totalLongTermAssets, totalEquity } = this;
        return this.getArrayDivide(totalLongTermAssets, totalEquity);
    }

    getArraySum(array1, array2, options = { minus: false }) {
        //add element values of arrays
        return this.yearsFormatted.map(
          (year, index) =>
            ({
              date: year,
              value: options.minus ?
                Number(array1[index].value) - Number(array2[index].value) :
                Number(array1[index].value) + Number(array2[index].value),
            })
        );
    }

    getArrayDivide(array1, array2) {
        //add element values of arrays
        return this.yearsFormatted.map(
          (year, index) =>
            ({
              date: year,
              value: typeof array1 === "number" ?
                array1 / Number(array2[index].value) :
                Number(array1[index].value) / Number(array2[index].value),
            })
        );
    }

    getMultiply(array, num) {
        //add element values of arrays
        return this.yearsFormatted.map(
          (year, index) =>
            ({
              date: year,
              value: Number(array[index].value) * num,
            })
        );
    }
}
